import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

const DEFAULT_FOCUS_BACKGROUND = `linear-gradient(
  0deg,
  hsl(230deg, 100%, 50%),
  hsl(240deg, 100%, 70%)
)`;

export interface Props
  extends React.HTMLAttributes<HTMLButtonElement> {
  active: boolean;
  handleToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
  // Subtle but key detail: there should be a 2px gap between the ball and the track. We fake this by giving the ball a 2px outline matching the color of the background. By default it uses --color-background, but I can specify an override:
  // NOTE: This was previously called `backdropColor`.
  backgroundColor?: string;
  handleFocusColor?: string | null;
  handleColor?: string;
  handleSize?: number;
}

const Toggle = ({
  active,
  handleToggle,
  backgroundColor,
  handleFocusColor = DEFAULT_FOCUS_BACKGROUND,
  handleColor = 'var(--color-text)',
  handleSize = 16,
  style = {},
  ...delegated
}: Props) => {
  const borderWidth = 2;
  const handleBoxSize = handleSize + borderWidth * 2;

  const padding = handleBoxSize * 0.1;
  const width = handleBoxSize * 2 + padding * 2;

  const wrapperStyle = {
    width,
    padding,
    '--radius': handleBoxSize * 0.25 + 'px',
    ...style,
  };

  const ballStyle = useSpring({
    width: handleBoxSize,
    height: handleBoxSize,
    transform: active ? `translateX(100%)` : `translateX(0%)`,
    config: {
      tension: 370,
      friction: 16,
      mass: 0.75,
    },
  });

  return (
    <Wrapper
      type="button"
      aria-pressed={active}
      style={wrapperStyle}
      onClick={handleToggle}
      {...delegated}
    >
      <Ball
        style={{
          ...ballStyle,
          '--outline-color': backgroundColor,
          '--handle-color': handleColor,
          '--handle-focus-color': handleFocusColor || handleColor,
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled(animated.button)`
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    z-index: 0;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
  }

  &:after {
    content: '';
    position: absolute;
    z-index: 1;
    top: 0;
    left: var(--radius);
    right: var(--radius);
    bottom: 0;
    margin: auto;
    height: 3px;
    background: var(--color-gray-300);
    border-radius: 10px;
  }

  &:focus-visible {
    outline: 2px auto var(--color-text);
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const Ball = styled(animated.div)`
  position: relative;
  z-index: 2;
  border-radius: 50%;
  background: var(--handle-color);
  outline: 2px solid var(--outline-color, var(--color-background));

  ${Wrapper}:focus-visible & {
    background: var(--handle-focus-color);
  }
`;

export default Toggle;
